// GraphQL mutation to create careplan, create or update careplan diagnosis
import React, { useCallback, useState, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_OR_UPDATE_CAREPLAN_DIAGNOSIS, GET_CAREPLAN_AND_DIAGNOSIS } from '../Queries';

import { useFormikContext, FormikProps } from 'formik';

import { DiagnosisRow, diagnosisRowToDiagnosisInput } from './useDiagnosisData';
import { useHistory } from 'react-router-dom';
import { GET_INTAKE_CODE_MAPPING } from '../../Queries';
import { codeToIntakePageMapping } from 'op-pages/RO/Careplan/DiagnosisPages/Utils';

interface CareplanHookReturn {
  careplan:
    | {
        careplan: {
          id: string;
          diagnosis: {
            id: string;
          };
          careplanStatus: string;
          isPrimDiagnosisOwner: boolean;
          isMetsDiagnosisOwner: boolean;
          submittedAt?: string;
        };
      }
    | undefined;
  handleContinue: (primaryDiagnosisRow: DiagnosisRow | null, metastasisDiagnosisRow: DiagnosisRow | null) => void;
}

export const useCareplan = (patientId: string, careplanId: string): CareplanHookReturn => {
  const history = useHistory();
  const { setFieldValue }: FormikProps<any> = useFormikContext();
  const { data: careplan } = useQuery(GET_CAREPLAN_AND_DIAGNOSIS, {
    variables: { careplanId },
    skip: !careplanId || careplanId === 'create',
    fetchPolicy: 'cache-and-network',
  });
  const { data: intakeCodeMapping, error: intakeMappingError } = useQuery(GET_INTAKE_CODE_MAPPING, {
    fetchPolicy: 'cache-and-network',
  });
  const [createOrUpdateCareplanDiagnosisMutation] = useMutation(CREATE_OR_UPDATE_CAREPLAN_DIAGNOSIS, {
    refetchQueries: [GET_CAREPLAN_AND_DIAGNOSIS],
    awaitRefetchQueries: true,
  });

  const navigateToNext = useCallback(
    (cp: { id: string; diagnosis: { id: string }; isPrimDiagnosisOwner: boolean; isMetsDiagnosisOwner: boolean }) => {
      const diagnosisUrl = `/radiation/patient/${patientId}/careplan/${cp.id}/diagnosis/${cp.diagnosis.id}`;
      const TNMStagingUrl = `/radiation/patient/${patientId}/careplan/${cp.id}/diagnosis/${cp.diagnosis.id}/tnmstaging`;
      const TreatmentInfoUrl = `/radiation/patient/${patientId}/careplan/${cp.id}/treatmentInfo`;
      if (cp.isPrimDiagnosisOwner || cp.isMetsDiagnosisOwner) {
        history.replace(diagnosisUrl);
        history.push(TNMStagingUrl);
      } else {
        history.replace(diagnosisUrl);
        history.push(TreatmentInfoUrl);
      }
    },
    [history, patientId],
  );

  // Memoize the code to stream mapping to prevent unnecessary recalculations
  const codeToStream = useMemo(() => {
    if (!intakeCodeMapping?.intakeCodeMapping?.length) {
      return {};
    }
    return intakeCodeMapping.intakeCodeMapping.reduce((current: any, candidate: any): any => {
      const name = candidate.intake.name;
      const code = candidate.diagnosisCode.diagnosisCode;
      current[code] = name;
      return current;
    }, {});
  }, [intakeCodeMapping]);

  React.useEffect(() => {
    /* 
    This is required to update the formik context
    Used for:
      * left hand side table to show the latest data
      * intake page to show the correct page 
    TODO: update the code not use formik context either roll our own or use GQL 
     */
    if (careplan?.careplan?.diagnosis) {
      const diagnosis = careplan.careplan.diagnosis;
      setFieldValue('diagnosis', diagnosis);

      // Intake
      if (Object.keys(codeToStream).length > 0 && diagnosis) {
        const primaryDiagnosis = diagnosis?.isPrimaryDiagnosis ? diagnosis : diagnosis?.relatedPrimaryDiagnosis;
        const intakeNameMapped = codeToIntakePageMapping(
          primaryDiagnosis?.diagnosisCode ? primaryDiagnosis?.diagnosisCode?.diagnosisCode : '',
          codeToStream,
        );
        setFieldValue('intakePage', intakeNameMapped);
      }
    }
  }, [careplan, codeToStream, setFieldValue]);

  const createOrUpdateDiagnosis = useCallback(
    async (
      patientId: string,
      careplanId: string,
      primaryDiagnosisRow: DiagnosisRow | null,
      metastasisDiagnosisRow: DiagnosisRow | null,
    ) => {
      const primaryDiagnosis = diagnosisRowToDiagnosisInput(primaryDiagnosisRow, true);
      const metastasisDiagnosis = diagnosisRowToDiagnosisInput(metastasisDiagnosisRow, false);

      // Only include metastasisDiagnosis in variables if it's actually selected
      const variables: any = { patientId, careplanId, primaryDiagnosis };
      if (metastasisDiagnosisRow) {
        variables.metastasisDiagnosis = metastasisDiagnosis;
      }

      createOrUpdateCareplanDiagnosisMutation({
        variables,
      }).then((data) => {
        const updatedCareplan = data.data.createOrUpdateCareplanDiagnosis.careplan;
        navigateToNext(updatedCareplan);
      });
    },
    [createOrUpdateCareplanDiagnosisMutation, navigateToNext],
  );

  const handleContinue = useCallback(
    (primaryDiagnosisRow: DiagnosisRow | null, metastasisDiagnosisRow: DiagnosisRow | null) => {
      if (careplan?.careplan?.diagnosis) {
        const currentDiagnosis = careplan.careplan.diagnosis;
        const currentPrimaryCodeId = currentDiagnosis.isPrimaryDiagnosis
          ? currentDiagnosis?.diagnosisCode?.id
          : currentDiagnosis?.relatedPrimaryDiagnosis?.diagnosisCode?.id;
        const currentMetsCodeId = currentDiagnosis.isPrimaryDiagnosis ? undefined : currentDiagnosis?.diagnosisCode?.id;

        const selectedPrimaryCodeId = primaryDiagnosisRow?.diagnosisCodeId as string | undefined;
        const selectedMetsCodeId = metastasisDiagnosisRow?.diagnosisCodeId as string | undefined;

        const primarySame = selectedPrimaryCodeId ? selectedPrimaryCodeId === currentPrimaryCodeId : true;
        const metsSame = selectedMetsCodeId ? selectedMetsCodeId === currentMetsCodeId : true;

        if (primarySame && metsSame) {
          navigateToNext(careplan.careplan);
          return;
        }
      }

      window.console.log('change');

      createOrUpdateDiagnosis(patientId, careplanId, primaryDiagnosisRow, metastasisDiagnosisRow);
    },
    [createOrUpdateDiagnosis, patientId, careplanId, careplan, navigateToNext],
  );

  return { careplan, handleContinue };
};
